import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { ApiResponse, ContactNoteDto, CreateContactNoteBody } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { contactNotesQueryKey } from '@/hooks/queries/use-contact-notes-query';

interface UpdateContactNoteVars {
  noteId: string;
  body: string;
}

interface UpdateContactNoteContext {
  previous?: ContactNoteDto[];
}

const updateContactNote = async (
  creatorIdentityId: string,
  noteId: string,
  body: CreateContactNoteBody
): Promise<ContactNoteDto> => {
  const { data } = await api.patch<ApiResponse<ContactNoteDto>>(
    `/dashboard/contacts/${creatorIdentityId}/notes/${noteId}`,
    body
  );

  if (!data.data) {
    throw new Error('Contact note not updated');
  }

  return data.data;
};

/**
 * Edits a note's body, optimistically swapping the cached row. Mirrors
 * useCreateContactNote's cache conventions (cancel + snapshot + setQueryData,
 * rollback on error, invalidate on settled).
 */
export const useUpdateContactNote = (creatorIdentityId: string | null) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = contactNotesQueryKey(creatorIdentityId);

  return useMutation<
    ContactNoteDto,
    AxiosError<ApiResponse>,
    UpdateContactNoteVars,
    UpdateContactNoteContext
  >({
    mutationFn: ({ noteId, body }) => updateContactNote(creatorIdentityId!, noteId, { body }),
    onMutate: async ({ noteId, body }) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ContactNoteDto[]>(queryKey);

      queryClient.setQueryData<ContactNoteDto[]>(queryKey, (current) =>
        (current ?? []).map((note) => (note.id === noteId ? { ...note, body } : note))
      );

      return { previous };
    },
    onError: (error, _vars, context) => {
      queryClient.setQueryData(queryKey, context?.previous);

      toaster.error({
        title: t('contactDrawer.notes.updateError'),
        description: error.response?.data?.message || t('common.errors.unexpected'),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
